/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { tokens } from "../../../../theme";
import Header from "../../../../components/Header";
import DataTable from "../../../../components/DataTable";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DataGridButton from "../../../../components/DataGridButton";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { dataGridButtonTypography } from "../../../../styles/DataGridStyles";
import { ChevronRight, Close, Sync } from "@mui/icons-material";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { OsImageBuildsStyles, buildDrawerStyles } from "../../styles";
import {
  closeButtonStyles,
  dataGridButtonStyles,
} from "../../../../styles/buttonStyles";
import {
  useDeleteOsImageBuild,
  useListAllOsImageBuilds,
  useRetryOsImageBuild,
} from "../../../../api/softwareImages";
import TimeCell from "../../../../components/TimeCell";
import { CloudIcon, EngineIcon } from "../../Utils";
import DetailPanelContent from "./DetailedPanelContent";
import useResizableDrawerWidth from "../../../../components/useResizableDrawerWidth";
import DrawerResizeHandle from "../../../../components/DrawerResizeHandle";
import DrawerSnapToggle from "../../../../components/DrawerSnapToggle";
import { useListResources } from "../../../../api/iam";
import { useNotification } from "../../../../components/Notification";
import { useConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { permissionListIncludes } from "../../../../utils/helperFunctions";
import { useReportModal } from "../../../../components/ReportModal";
import { tabbedDataGridPage } from "../../../../styles/commonPageStyles";

const buildsAdapter = (builds) => {
  return builds.map((build) => {
    return {
      ...build,
      engine: build?.inputData?.engine,
      releaseBranch: build?.inputData?.releaseBranch,
      versionName: build?.inputData?.versionName,
      tenantName: build?.inputData?.tenantUrl?.split(".")?.[0],
    };
  });
};

const OsImageBuilds = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const [builds, setBuilds] = useState([]);
  const [reload, setReload] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);
  const isDesktopDrawer = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const {
    width: drawerWidth,
    isWide: isDrawerWide,
    toggleWide: toggleDrawerWide,
    handleProps: resizeHandleProps,
  } = useResizableDrawerWidth({
    storageKey: "os-build-details",
    defaultWidth: 720,
    maxWidth: 1100,
    enabled: isDesktopDrawer,
  });

  const { notificationComponent, displayNotification } = useNotification();
  const { reportModal, setReportModalOpen } = useReportModal();

  const {
    postData: getBuilds,
    response: getBuildsResp,
    isLoading: isLoadingBuilds,
  } = useListAllOsImageBuilds();

  const {
    confirmationDialog: deleteDialog,
    showConfirmationDialog: showDeleteDialog,
  } = useConfirmationDialog(
    "Delete Build",
    useDeleteOsImageBuild,
    displayNotification,
    getBuilds
  );

  const {
    confirmationDialog: retryDialog,
    showConfirmationDialog: showRetryDialog,
  } = useConfirmationDialog(
    "Retry Build",
    useRetryOsImageBuild,
    displayNotification,
    getBuilds
  );

  const deletePermissionList = useListResources(
    "/apps/tessell/software-images/db-software/builds",
    "DELETE",
    "DELETE",
    [reload]
  );

  const retryPermissionList = useListResources(
    "/apps/tessell/software-images/db-software/builds",
    "POST",
    "RETRY",
    [reload]
  );

  useEffect(() => {
    getBuilds();
  }, [reload]);

  useEffect(() => {
    setBuilds(buildsAdapter(getBuildsResp?.osImageBuilds || []));
  }, [getBuildsResp]);

  const columns = [
    {
      field: "details",
      width: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => "",
      renderCell: ({ row }) => (
        <IconButton size="small" onClick={() => setDetailsRow(row)}>
          <ChevronRight fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: "patchVersionName",
      flex: 2,
      cellClassName: "name-column--cell",
      renderHeader: () => "Name",
      renderCell: ({ row: { versionName } }) => versionName || "",
    },
    {
      field: "jiraId",
      flex: 1.5,
      renderHeader: () => "Jira Id",
      renderCell: ({ row: { jiraId } }) => jiraId || "",
    },
    {
      field: "engine",
      flex: 1,
      renderHeader: () => "Engine",
      renderCell: ({ row: { engine } }) => <EngineIcon engine={engine} />,
    },
    {
      field: "clouds",
      flex: 2,
      renderHeader: () => "Clouds",
      renderCell: ({ row: { inputData } }) => (
        <>
          {Object.keys(inputData?.clouds || {})?.map((cloud, i) => (
            <>
              <CloudIcon cloud={cloud?.toLowerCase()} />{" "}
            </>
          ))}
        </>
      ),
    },
    {
      field: "releaseBranch",
      renderHeader: () => "Branch",
      flex: 1,
      renderCell: ({ row: { releaseBranch } }) => releaseBranch || "",
    },

    {
      field: "triggeredBy",
      renderHeader: () => "Triggered By",
      flex: 3,
      renderCell: ({ row: { triggeredBy } }) => triggeredBy || "",
    },
    {
      field: "dateCreated",
      renderHeader: () => "Created",
      flex: 1.3,
      minWidth: 118,
      renderCell: ({ row: { dateCreated } }) => (
        <TimeCell value={dateCreated} />
      ),
    },
    {
      field: "dateModified",
      renderHeader: () => "Modified",
      flex: 1.3,
      minWidth: 118,
      renderCell: ({ row: { dateModified } }) => (
        <TimeCell value={dateModified} />
      ),
    },
    {
      field: "actions",
      type: "actions",
      flex: 0.5,
      getActions: ({ row }) =>
        [
          permissionListIncludes(deletePermissionList, row?.id) && (
            <GridActionsCellItem
              label="Delete"
              onClick={() =>
                showDeleteDialog("Permanently delete this build data?", row?.id)
              }
              showInMenu
            />
          ),
        ].filter(Boolean),
    },
  ];

  return (
    <Box sx={OsImageBuildsStyles.mainBox}>
      {notificationComponent}
      {deleteDialog}
      {retryDialog}
      {reportModal}
      <Grid sx={OsImageBuildsStyles.headerGrid} container columns={2}>
        <Grid xs={1}>
          <Header
            title="OS Image Builds"
            subtitle="View and manage os image builds"
          />
        </Grid>
        <Grid xs={1}>
          <Box sx={tabbedDataGridPage.topButtonStack}>
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => setReload((prev) => !prev)}
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
          </Box>
        </Grid>
      </Grid>

      <DataTable
        height="100%"
        sx={OsImageBuildsStyles.dataTable}
        loading={isLoadingBuilds}
        rows={builds}
        columns={columns}
      />

      <Drawer
        anchor="right"
        open={Boolean(detailsRow)}
        onClose={() => setDetailsRow(null)}
        PaperProps={{
          sx: {
            ...buildDrawerStyles.paper(colors, drawerWidth),
            overflow: "visible",
          },
        }}
      >
        <DrawerResizeHandle
          {...resizeHandleProps}
          ariaLabel="Resize OS build details drawer"
        />
        <DrawerSnapToggle
          enabled={isDesktopDrawer}
          isWide={isDrawerWide}
          onClick={toggleDrawerWide}
          ariaLabel="Expand OS build details drawer to 75% width"
          collapseLabel="Collapse OS build details drawer"
          sx={{ position: "absolute", left: "-12px", top: "12px", zIndex: 9 }}
        />
        <Box sx={buildDrawerStyles.header(colors)}>
          <Box>
            <Typography sx={buildDrawerStyles.title(colors)}>
              {detailsRow?.versionName}
            </Typography>
            <Typography sx={buildDrawerStyles.subtitle(colors)}>
              {detailsRow?.id}
            </Typography>
          </Box>
          <IconButton
            sx={closeButtonStyles(colors)}
            onClick={() => setDetailsRow(null)}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={buildDrawerStyles.body}>
          {detailsRow && (
            <DetailPanelContent
              row={detailsRow}
              showRetryDialog={showRetryDialog}
              retryPermissionList={retryPermissionList}
              setReportModalOpen={setReportModalOpen}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default OsImageBuilds;
